/* eslint-disable prefer-arrow-callback */
import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _delay from 'lodash/delay';
import _get from 'lodash/get';
// Models
import mBooking from 'models/cpm/booking';
import mFinance from 'models/cpm/finance';
// HOCs
import withConfirmDialog from 'hocs/WithConfirmDialog';
import withLoading from 'hocs/WithLoading';
import withPermissions from 'hocs/WithPermissions';
import withMessageDialog from 'hocs/WithMessageDialog';
import withTitle from 'hocs/WithTitle';
// Hooks
import useResponsive from 'hooks/useResponsive';
// Custom
import {
  getAlertings,
  refreshList,
  cpmListMyBookings,
  cpmCancelMyBooking,
} from 'actions';
import { timezoneUtils } from 'utils/DateUtils';
import { listShape } from 'constants/PropTypesShapes';
import MainListTemplate from 'components/templates/MainListTemplate';
import { navigateToShow } from 'cpmMyBookings/navigation';
import MyBookingRow from 'cpmMyBookings/assets/MyBookingRow';
import MyBookingFilters from 'cpmMyBookings/assets/MyBookingFilters';
import { CpmClientContext } from 'components/misc/Contexts';

const MyBookingsList = ({
  cpmBookingList,
  cpmBookingCancel,
  handleDisplayMessage,
  handleStartLoading,
  dispatchListMyBookings,
  handleDisplayConfirm,
  dispatchCancelBooking,
  dispatchRefreshList,
}) => {
  const { setShowBookButton } = React.useContext(CpmClientContext);
  const isMobile = useResponsive();
  const showCancelConfirmation = React.useRef(false);

  React.useEffect(() => setShowBookButton(true), []);

  /* ========================================================= */
  /* CANCEL BOOKING */
  /* ========================================================= */

  React.useEffect(function refreshListAfterBookingCancellation() {
    if (cpmBookingCancel.cancelled && showCancelConfirmation.current) {
      const isFree = mFinance.lastPurchasePriceIsFree(cpmBookingCancel.result) || mFinance.lastPurchase(cpmBookingCancel.result).totalAmountInclTax === 0;
      handleDisplayMessage(isFree ? 'success.myBookingFreeCancelled' : 'success.myBookingCancelled', {}, 'info');
      showCancelConfirmation.current = false;
      dispatchRefreshList();
    }
  }, [cpmBookingCancel.cancelled]);

  /* ========================================================= */
  /* HANDLERS */
  /* ========================================================= */

  const handleCancelBooking = (booking) => {
    const isFree = mFinance.lastPurchasePriceIsFree(booking) || mFinance.lastPurchase(booking).totalAmountInclTax === 0;
    handleDisplayConfirm({ id: isFree ? 'confirm.cancelBookingFree' : 'confirm.cancelBooking' },
      () => {
        dispatchCancelBooking({ id: booking.id });
        showCancelConfirmation.current = true;
      },
      {
        confirmLabel: 'confirm',
      });
  };

  const handleRowDbleClick = (booking) => {
    const DELAY_BEFORE_NAVIGATION = 500;
    if (booking) {
      // Display loader and display carpark
      handleStartLoading();
      _delay(() => navigateToShow(booking.reference), DELAY_BEFORE_NAVIGATION);
    }
  };

  const doGetRowComp = (props) => {
    // eslint-disable-next-line react/prop-types
    const { booking } = props;
    return (
      <MyBookingRow
        {...props}
        onCancel={handleCancelBooking}
        // eslint-disable-next-line react/prop-types
        timezone={booking.carpark.timezone}
      />
    );
  };

  /* ========================================================= */
  /* SUB COMPONENTS */
  /* ========================================================= */

  const PageTitle = (
    <div className={`d-flex justify-content-${isMobile ? 'center text-center' : 'end'} bookPageTitle`}>
      <LocalizedMessage id="title.myBookings" />
    </div>
  );

  /* ========================================================= */
  /* UI */
  /* ========================================================= */
  return (
    <div className="d-flex flex-column">
      {PageTitle}
      <MainListTemplate
        title=""
        name={mBooking.name}
        listFeatures={mBooking.listFeatures}
        elementsData={{
          loading: cpmBookingList.loading,
          elements: cpmBookingList.elements,
          handleGetList: dispatchListMyBookings,
        }}
        onRowDbleClick={handleRowDbleClick}
        rowComp={doGetRowComp}
        withActions
        withPadding={false}
        nbActions={isMobile ? 1 : 2}
        withDivider={false}
        filtersComp={<MyBookingFilters timezone={timezoneUtils.userTimezone()} />}
      />
    </div>
  );
};

MyBookingsList.propTypes = {
  cpmBookingList: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    elements: listShape.isRequired,
  }).isRequired,
  cpmBookingCancel: PropTypes.shape({
    cancelled: PropTypes.bool.isRequired,
    result: PropTypes.object,
  }).isRequired,
  handleDisplayConfirm: PropTypes.func.isRequired,
  handleDisplayMessage: PropTypes.func.isRequired,
  handleStartLoading: PropTypes.func.isRequired,
  dispatchCancelBooking: PropTypes.func.isRequired,
  dispatchListMyBookings: PropTypes.func.isRequired,
  dispatchRefreshList: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  cpmBookingList: _get(state, 'cpmBooking.list', null),
  cpmBookingCancel: _get(state, 'cpmBooking.cancel', null),
});

const mapDispatchToProps = (dispatch) => ({
  dispatchRefreshList: () => dispatch(refreshList()),
  dispatchListMyBookings: (pagination, sort, filters) => {
    dispatch(
      cpmListMyBookings(mBooking.queListMyBookings, {
        pagination,
        sort,
        filters,
      }),
    );
  },
  dispatchCancelBooking: (data) => dispatch(cpmCancelMyBooking(mBooking.mutCancelMyBooking, data)),
  dispatchGetAlertings: () => dispatch(getAlertings()),
});

module.exports = compose(
  withConfirmDialog,
  withLoading,
  withPermissions,
  withMessageDialog,
  withTitle('pageTitle.myBookings'),
  connect(mapStateToProps, mapDispatchToProps),
)(MyBookingsList);



// WEBPACK FOOTER //
// ./routes/my_bookings/routes/list/components/MyBookingsList.js